import React from 'react'
import PropTypes from 'prop-types'
import { subYears } from 'date-fns'

// @material-ui
import { Grid, Typography, Collapse, TextField } from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'

// core
import { withLang } from '_core/hocs/withLang'
import { TextInput } from '_core/components/TextInput'
import { PhoneInput } from '_core/components/PhoneInput'
import { FileInput } from '_core/components/FileInput'
import { AvatarUpload } from '_core/components/AvatarUpload'
import { CalendarInput } from '_core/components/CalendarInput'
import { FormValidator } from '_core/components/FormValidator'
import { StepsControls } from '_core/components/StepsControls'
import { ReactDadata } from '_core/components/CustomDadata'
import { GenderInput } from '_core/components/GenderInput'

import 'react-dadata/dist/react-dadata.css'

const DATE_DISPLAY_FORMAT = 'D MMMM YYYY'
const MIN_DATE = subYears(new Date(), 90)
const MAX_DATE = subYears(new Date(), 15)

const RawProfileInfoFormView = props => {
  const {
    method,
    fields,
    fieldsOrder,
    fieldsOptions,
    brokenVerifTypes,
    userInfo,
    onSubmit,
    clearValue,
    resetValue,
    getLangString: l,
    langInfo,
  } = props
  // const classes = useProfileInfoFormStyle()
  // const [query, setQuery] = useState(null)
  // const classes = useProfileInfoFormStyle()

  const renderField = (name, viewProps, validatorProps) => {
    switch (name) {
	    case 'login2':
	    case 'email':
	    case 'address':
	    case 'lastname':
	    case 'firstname':
	    case 'mail_index':
	    case 'patronymic':
	    case 'passp_serial':
	    case 'passp_number':
	    case 'visitor_date':
	    case 'properties.coowner_mobile':
	    case 'properties.coowner_email':
	    case 'properties.coowner_firstname':
	    case 'properties.coowner_lastname':
	    case 'properties.coowner_patronymic':
	    case 'properties.facebook':
	    case 'properties.vk':
	    case 'properties.ok':
	    case 'properties.telegram':
	    case 'properties.watsapp':
	    case 'properties.instagram':
	    case 'properties.youtube':
	    case 'properties.tiktok':
	    case 'properties.viber':
	    case 'properties.twitter':
        return <TextInput {...viewProps} {...validatorProps} />

      case 'mobile':
        return <PhoneInput {...viewProps} {...validatorProps} />

      case 'gender':
        return <GenderInput {...viewProps} {...validatorProps} />

      case 'photo':
        return (
          <FileInput
            {...viewProps}
            {...validatorProps}
            category="personal"
            Component={AvatarUpload}
            userInfo={userInfo}
            onClear={() => validatorProps.onChange(clearValue)}
            onReset={() => validatorProps.onChange(resetValue)}
          />
        )

      case 'regionaddress':
        return (
          <ReactDadata
            customInput={props => {
              return <TextField label={l(validatorProps.label)} {...props} />
            }}
            value={validatorProps.value}
            defaultQuery={validatorProps.value}
            query={validatorProps.value}
            placeholder={l('r.core.good.checkout.settlementtitle')}
            onChange={validatorProps.onChangeSave}
            lang={props.langInfo != null ? props.langInfo.code : 47}
            onInputChange={(value, e) => validatorProps.onChange(e)}
          />
        )

      case 'birth_date':
        // eslint-disable-next-line no-case-declarations
        return (
          <CalendarInput
            noClear
            format={DATE_DISPLAY_FORMAT}
            inputProps={{
              label: validatorProps.label,
            }}
            popupProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            }}
            calendarProps={{
              locale: langInfo.code,
              dateFormat: 'dd MMMM yyyy',
              peekNextMonth: true,
              showMonthDropdown: true,
              showYearDropdown: true,
              scrollableYearDropdown: true,
              dropdownMode: 'select',
              minDate: MIN_DATE,
              maxDate: MAX_DATE,
            }}
            {...viewProps}
            {...validatorProps}
            onChange={(value) => validatorProps.onChange && validatorProps.onChange(value.format('DD.MM.YYYY'))}
            value={validatorProps.value}
          />
        )

      default:
        return null
    }
  }

  const renderFields = form =>
    fieldsOrder.reduce((result, name) => {
      if (fields[name] instanceof Object) {
        const { viewProps, gridProps } = fieldsOptions[name]

        result.push(
          <Grid item={true} xs={12} md={6} key={name} {...gridProps}>
            {renderField(name, viewProps, form[name])}
          </Grid>
        )
      }

      return result
    }, [])

  const renderVerifAlert = () => (
    <Alert severity="warning">
      <AlertTitle>
        <Typography variant="h6">
          {l('r._.profile.info.text.verifalert')}:
        </Typography>
      </AlertTitle>

      {brokenVerifTypes
        .map(type => l(`r._.profile.verify.${type}.titleblank`))
        .join(', ')}
    </Alert>
  )

  return (
    <form onSubmit={onSubmit}>
      <FormValidator
        method={method}
        fields={fields}
        checkIfChanged={true}
        render={({ form, canSubmit }) => (
          <>
            <Grid container={true}>{renderFields(form)}</Grid>

            <Collapse in={canSubmit && brokenVerifTypes.length > 0}>
              {renderVerifAlert()}
            </Collapse>

            <StepsControls
              hasBackButton={false}
              nextButtonText="r._.profile.info.action.submit"
              nextButtonProps={{
                type: 'submit',
                // disabled: !canSubmit,
              }}
            />
          </>
        )}
      />
    </form>
  )
}

RawProfileInfoFormView.defaultProps = {}

RawProfileInfoFormView.propTypes = {
  // self props
  method: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  userInfo: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  fieldsOptions: PropTypes.object,
  fieldsOrder: PropTypes.arrayOf(PropTypes.string),
  brokenVerifTypes: PropTypes.arrayOf(PropTypes.string),
  clearValue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  resetValue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const ProfileInfoFormView = withLang(RawProfileInfoFormView)
